import {
  BooleanField,
  createBooleanWithDependentItem,
  createDependencies,
  createValidationResult,
  IModelContext,
  IValidationResult,
  ModelGroup,
  ModelItem,
  ModelItemOptions,
  NumberField,
  requiredValueOptionsValidation,
  StringField,
  ValidationFunction,
  ValueOptions
} from '@mng/model';
import { UnangemessenesProdukt } from '../../antrag.model.types';
import {
  isAufgrundVonBeratungVerzichtNotRequired,
  isSonstigesGruendeDescRequired
} from './veranlagungberatung.validation';

// TODO ausbauen nicht sicher ob doch benötigt (derzeit ist veranlagungBeratung auf false gestellt)
export class VeranlagungBeratungModelDef {
  static createVeranlagungBeratungModel(): ModelGroup {
    const unangemessenesProduktModelGroup: ModelGroup = this.createUnangemessenesProduktModel();

    const unangemessenesProduktAusgewaehltField: BooleanField = createBooleanWithDependentItem(
      'unagemessenesProduktAusgewaehlt',
      'Der Vermittler ist der Auffassung, dass das von mir gewählte Produkt nicht für meine Bedürfnisse angemessen ist.',
      unangemessenesProduktModelGroup
    );

    const verzichtetAufBeratungField: BooleanField = new BooleanField(
      'verzichtetAufBeratung',
      `Ich bin nicht bereit, vollständige Informationen zu
                    meinem persönlichen Veranlagungsprofil offen zu legen. Ich nehme zur Kenntnis, dass dadurch eine
                    Beratung im Sinne des § 75 Abs. 2 VAG nicht möglich ist. Dies erfolgt nicht auf Veranlassung des Vermittlers.`
    );

    const beratungsverzichtModelGroup: ModelGroup = new ModelGroup(
      'beratungsverzicht',
      `Kundenerklärung bei Beratungsverzicht`
    )
      .addItems(verzichtetAufBeratungField, unangemessenesProduktAusgewaehltField)
      .addGroupObject(unangemessenesProduktModelGroup);

    const valueGroups: ModelGroup[] = [
      this.createVeranlagungskenntnisseModel(),
      this.createVeranlagungszieleModel(),
      this.createVeranlagungsHorizontModel(),
      this.createfinanzielleMoeglichkeitenModel(),
      this.createVermoegensanteilModel(),
      this.createRisikobereitschaftModel()
    ];

    // DEPENDENCIES bei Beratungsverzicht
    createDependencies(valueGroups, isAufgrundVonBeratungVerzichtNotRequired, true);

    const veranlagungBeratungModel: ModelGroup = new ModelGroup(
      'veranlagungBeratung',
      `Fragen zum Veranlagungsprofil des Versicherungsnehmers im Hinblick auf Kapitalanlagen`
    )
      .addGroupObjects(valueGroups)
      .addGroupObject(beratungsverzichtModelGroup);

    // veranlagungBeratungModel.options = new ModelItemOptions([veranlagungBeratungValidation]);

    return veranlagungBeratungModel;
  }

  static createVeranlagungskenntnisseModel(): ModelGroup {
    const veranlagungsProfilModel: ModelGroup = new ModelGroup(
      'veranlagungsProfil',
      `1. Verfügen Sie über
               Kenntnisse bzw. Erfahrungen auf dem Gebiet der Veranlagung in Wertpapieren bzw. Investmentfonds?`
    ).addItems(
      new NumberField('aktien', 'Aktien').withOptions(
        new ModelItemOptions([requiredValueOptionsValidation]).withValueOptions(kenntnisseValueOptions)
      ),
      new NumberField('wertpapiere', 'Wertpapiere').withOptions(
        new ModelItemOptions([requiredValueOptionsValidation]).withValueOptions(kenntnisseValueOptions)
      ),
      new NumberField('anleihe', 'Anleihe').withOptions(
        new ModelItemOptions([requiredValueOptionsValidation]).withValueOptions(kenntnisseValueOptions)
      ),
      new NumberField('sonstige', 'Sonstige').withOptions(
        new ModelItemOptions([requiredValueOptionsValidation]).withValueOptions(kenntnisseValueOptions)
      )
    );
    return veranlagungsProfilModel;
  }

  static createVeranlagungszieleModel(): ModelGroup {
    const veranlagungsZieleModel: ModelGroup = new ModelGroup(
      'anlageziele',
      `2. Welche Anlageziele bzw.
              -motive verfolgen Sie im Rahmen der fondsgebundenen Lebensversicherung?`
    ).addItems(
      new NumberField('zielemotive', '').withOptions(
        new ModelItemOptions([requiredValueOptionsValidation]).withValueOptions(zieleValueOptions)
      )
    );
    return veranlagungsZieleModel;
  }

  static createVeranlagungsHorizontModel(): ModelGroup {
    const veranlagungsHorizontModel: ModelGroup = new ModelGroup(
      'anlagehorizont',
      '3. Welchen Veranlagungshorizont bevorzugen Sie?'
    ).addItems(
      new NumberField('horizont', '').withOptions(
        new ModelItemOptions([requiredValueOptionsValidation]).withValueOptions(horizontValueOptions)
      )
    );
    return veranlagungsHorizontModel;
  }

  static createfinanzielleMoeglichkeitenModel(): ModelGroup {
    const finanzielleMoeglichkeitenBetrag: NumberField = new NumberField(
      'veranlagungsBetrag',
      'Für die Veranlagung stehen mir insgesamt monatlich frei zur Verfügung € '
    ).withRequired();

    const finanzielleMoeglichkeitenModel: ModelGroup = new ModelGroup(
      'finanzmoeglichkeiten',
      '4. Veranlagungsbetrag'
    ).addItems(finanzielleMoeglichkeitenBetrag);

    return finanzielleMoeglichkeitenModel;
  }

  static createVermoegensanteilModel(): ModelGroup {
    const vermoegensanteilModel: ModelGroup = new ModelGroup(
      'vermoegensanteil',
      `5. Welchen Anteil hat diese
            beabsichtigte Investmentfondsanlage im Rahmen der fondsgebundenen Lebensversicherung an Ihrem Vermögen?`
    ).addItems(
      new NumberField('vermoegensProzentanteil', '').withOptions(
        new ModelItemOptions([requiredValueOptionsValidation]).withValueOptions(vermoegensanteilValueOptions)
      )
    );
    return vermoegensanteilModel;
  }

  static createRisikobereitschaftModel(): ModelGroup {
    const risikobereitschaftModel: ModelGroup = new ModelGroup(
      'risikobereitschaft',
      `6. Wie schätzen Sie Ihre
              persönliche Risikobereitschaft in Bezug auf Ihre finanziellen Mittel ein?`
    ).addItems(
      new NumberField('persoenlichesRisiko', '').withOptions(
        new ModelItemOptions([requiredValueOptionsValidation]).withValueOptions(risikobereitschaftValueOptions)
      )
    );
    return risikobereitschaftModel;
  }

  static createUnangemessenesProduktModel(): ModelGroup {
    const validation: ValidationFunction = (
      modelItem: ModelItem,
      modelContext: IModelContext = {}
    ): IValidationResult => {
      const data: UnangemessenesProdukt = modelContext.value;
      const valid = data && (data.finanzverhaeltnisseUnpassend || data.risikoUnpassend || data.sonstigegruende);
      return valid ? null : createValidationResult('unangemessenesProdukt');
    };

    const sonstigesGruendeUnpassend: BooleanField = new BooleanField('sonstigegruende', 'sonstige Gründe');
    const sonstigesGruendeDesc: StringField = new StringField('sonstigesDesc', 'Beschreibung')
      .withRequired()
      .withFunctionDependency(isSonstigesGruendeDescRequired);

    const unangemessenesProduktModel: ModelGroup = new ModelGroup(
      'unangemessenesProdukt',
      'Ich wurde vom Vermittler darüber informiert, dass'
    )
      .addItems(
        new BooleanField(
          'risikoUnpassend',
          `das mit der gewählten Produktvariante bzw. der gewählten
                                  Veranlagungsform verbundene Risiko nicht mit meinem Veranlagungsverhalten übereinstimmt`
        ),
        new BooleanField(
          'finanzverhaeltnisseUnpassend',
          `und/oder nicht meinen finanziellen Verhältnissen entspricht.`
        ),
        sonstigesGruendeUnpassend,
        sonstigesGruendeDesc
      )
      .withValidation(validation);

    return unangemessenesProduktModel;
  }
}

export const kenntnisseValueOptions: ValueOptions = new ValueOptions([
  { name: 'gut', value: 1 },
  { name: 'mittel', value: 2 },
  { name: 'schlecht', value: 3 },
  { name: 'keine', value: 4 }
]);

export const zieleValueOptions: ValueOptions = new ValueOptions([
  { name: 'Vorsorge', value: 1 },
  { name: 'Vermögenszuwachs', value: 2 },
  { name: 'Steuervorteile', value: 3 }
]);

export const horizontValueOptions: ValueOptions = new ValueOptions([
  { name: '10 Jahre', value: 1 },
  { name: '11 - 20 Jahre', value: 2 },
  { name: 'Mehr als 20 Jahre', value: 3 }
]);

export const einkommenBetragValueOptions: ValueOptions = new ValueOptions([
  { name: 'unter € 1.500,--', value: 1 },
  { name: '€ 1.500,-- bis € 3.600,--', value: 2 },
  { name: 'über € 3.600,--', value: 3 },
  { name: 'Veranlagungsbetrag', value: 4 }
]);

export const vermoegensanteilValueOptions: ValueOptions = new ValueOptions([
  { name: 'bis ca. 10%', value: 1 },
  { name: 'bis ca. 30%', value: 2 },
  { name: 'mehr als 30%', value: 3 }
]);

export const risikobereitschaftValueOptions: ValueOptions = new ValueOptions([
  { name: 'Niedrige', value: 1 },
  { name: 'Mittlere', value: 2 },
  { name: 'Hohe Risikobereitschaft', value: 3 }
]);
